<template lang="pug">
uc-page(:page="{ id: 'audio-popup' }" class="bg-primary-900/50" v-if="!isAudioDiscreet")
  template(v-if="firstPage.bg")
    uc-asset.blur.h-screen.w-screen.fixed.top-0.left-0.object-cover.max-w-none(
      :data-page-background="firstPage.id"
      :src="firstPageBg"
      alt=""
      autoplay="false"
    )

  .parallax-bg-wrapper.blur(
    :data-page-parallax="firstPage.id"
    v-else-if="firstPage.parallax"
  )
    .parallax-bg.h-screen.w-screen.top-0.left-0
      img.h-screen.w-screen.fixed.object-cover.max-w-none(
        :data-page-background="firstPage.id"
        :data-page-parallax-background="firstPage.id"
        :src="firstPage.parallax"
        alt=""
      )

  .audio-popup-content.block.fixed.inset-0.m-auto.border-4.border-primary-700.rounded-xl.p-10.overflow-y-auto.h-fit.bg-gray-100(
    class="max-w-[90vw] md_max-w-[35em] max-h-[90vh]"
  )
    .text-gray-900.flex.flex-col.gap-8.m-auto.items-center.text-center.prose-stack
      uc-asset.hidden.md_block(
        class="w-[5rem]"
        :src="content?.img ?? 'uploads/library/svg/headphones-anim-loop5.p-blue.svg'"
        :size="5"
      )
      //- strings for translation script
      //- text="This course plays audio"
      //- text="This course includes audio narration by default. You can toggle this setting at any time by selecting the audio icon in the top corner."
      //- text="You can toggle this on/off at any time by selecting the settings icon in the top corner."
      //- text="Continue"
      //- text="Continue with audio disabled"
      uc-text.my-0.text-primary-700(
        :text="content?.heading ?? 'This course plays audio'" type="h2"
      )
      uc-text(
        :text="audioLocked ? 'This course includes audio narration by default. You must finish listening to the audio on each screen before you can continue.' : content?.['text-audio'] ?? 'This course includes audio narration by default. You can toggle this setting at any time by selecting the audio icon in the top corner.'"
        v-if="hasAudio || hasSpeech"
      )
      uc-text(
        :text="content?.['text-sfx'] ?? 'You can toggle this on/off at any time by selecting the settings icon in the top corner.'"
        v-else-if="hasSfx"
      )
      uc-button.btn-primary(
        :text="content?.['continue-on'] ?? 'Continue'"
        @click="continueWithAudio"
      )
      button.fancy-hover.prose.text-primary-700(@click="continueWithoutAudio" v-if="!audioLocked")
        uc-text(
          :text="content?.['continue-off'] ?? 'Continue with audio disabled'"
        )
</template>

<script setup lang="ts">
import { computed, inject, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { mute, muteAll, playSilence, unmuteAll } from '../lib/audio';
import useContent from '../stores/useContent';
import useAudio from '../stores/useAudio';

const props = defineProps<{
  firstPage: Page;
}>();

const hasAudio = inject('hasAudio');
const hasSpeech = inject('hasSpeech');
const hasSfx = inject('hasSfx');
const router = useRouter();
const content = useContent().compiled['audio-popup'];
const audioLocked = useContent().compiled.course['audiolocked'] || false;
const firstPageBg = computed(() => {
  // we want the first, non-video bg asset
  const videoFilter = ['.mp4', 'vimeo.com'];
  return props.firstPage.bg?.split(/ +/).filter(value => {
    const match = videoFilter.some(part => value.includes(part));
    return !match;
  })[0];
});
const isAudioDiscreet = useContent().compiled.course['audio-discreet'];

const { getAudioFilename } = useAudio();
const audioPath = computed(() => getAudioFilename('audio-popup'));

onMounted(() => {
  document.dispatchEvent(
    new CustomEvent('activeAudioChanged', {
      detail: { activeId: audioPath.value }
    })
  );
});

if (isAudioDiscreet) {
  continueWithDiscreetAudio();
}

function continueWithAudio() {
  unmuteAll();

  // play audio from a click to allow ios permission
  playSilence();

  navigateToFirstPage();
}

function continueWithDiscreetAudio() {
  // play audio from a click to allow ios permission
  playSilence();

  // only mute the narration audio for discreet audio
  mute();

  navigateToFirstPage();
}

function continueWithoutAudio() {
  // play audio from a click to allow ios permission
  playSilence();

  muteAll();

  navigateToFirstPage();
}

function navigateToFirstPage() {
  router.replace(props.firstPage.id);
}
</script>
